import React, { useState, useEffect, useCallback } from "react";

// recoil
import { useRecoilValue } from "recoil";
import { userCredentials } from "recoilState/GlobalState";

//Services
import { accesoGet, lugarDeviceGet, zonaGet } from "services/services-mia/miakey";

// utils
import XGrid from "components/UI/Grid/XGrid";
import { HeaderButton, Buttons } from "components";
import { Add, Cached, EditOutlined, BlockOutlined } from "@mui/icons-material";
import { DesactivarModal, InsertModal } from "./Actions";

// consts
import { kioscosDetalles as titles } from "consts/titulos-de-tablas";
import { ACTIONS } from "consts/Actions";

// styles
import s from "styles/pages/Pages.module.scss"
import Modal from "components/UI/Modal/Modal";

const FECHA_HOY = new Date().toISOString().substring(0, 10);

const tipoLabel = (cod) => {
    if (cod === "E") return "Entrada";
    if (cod === "S") return "Salida";
    if (cod === "A") return "Entrada / Salida";
    return cod;
};

const statusLabel = (cod) => {
    if (cod === "A") return "Alta";
    if (cod === "B") return "Baja";
    if (cod === "S") return "Suspendido";
    return cod;
};

export default function KioscoDetails({
    kioscoId,
    kioscoNro,
    kioscoIdentificacion,
    service
}) {
    const [modalDetails, setModalDetails] = useState({ isOpen: false });
    const [isLoading, setIsLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [zonas, setZonas] = useState([]);
    const [accesos, setAccesos] = useState([]);

    const credentials = useRecoilValue(userCredentials);

    const updateList = useCallback(async () => {
        setIsLoading(true);
        setRows([]);
        const { result } = await lugarDeviceGet({
            gateDeviceId: kioscoId
        }, credentials);
        setRows(result.map((item) => {
            const {
                gateLugarDeviceId,
                gateLugarDeviceFecha,
                gateLugarDeviceStatus,
                gateLugarDeviceTipo,
                gateLugarDeviceVersion,
                gateLugarId,
                gateLugarNombre,
                gateZonaId,
                gateZonaNombre,
                gateDeviceIdentificacion,
                gateLugarDeviceRequiereEntrada,
                gateLugarDeviceRequiereSalida,
            } = item;

            return {
                id: gateLugarDeviceId,
                gateLugarDeviceId,
                gateLugarDeviceFecha,
                gateLugarDeviceStatus,
                gateLugarDeviceTipo,
                gateLugarDeviceVersion,
                gateLugarId,
                gateLugarNombre,
                gateZonaId,
                gateZonaNombre,
                gateDeviceIdentificacion,
                gateLugarDeviceRequiereEntrada,
                gateLugarDeviceRequiereSalida,
                [titles[0]]: gateLugarDeviceFecha,
                [titles[1]]: gateZonaNombre,
                [titles[2]]: gateLugarNombre,
                [titles[3]]: tipoLabel(gateLugarDeviceTipo),
                [titles[4]]: gateLugarDeviceRequiereEntrada === "Y" ? "Sí" : "No",
                [titles[5]]: gateLugarDeviceRequiereSalida === "Y" ? "Sí" : "No",
                [titles[6]]: statusLabel(gateLugarDeviceStatus),
            }
        }));
        setIsLoading(false);
    }, [credentials, kioscoId]);

    useEffect(() => {
        updateList()
    }, [service, updateList]);

    useEffect(() => {
        if (Object.keys(credentials).length > 0 && service?.servicioCod) {
            (async () => {
                const { result: resultZonas } = await zonaGet({}, credentials)
                const { result: resultAccesos } = await accesoGet({}, credentials)
                const filteredAccesos = resultAccesos.filter((a) => a.gateZonaGlobal !== "Y");
                const availableZonaIds = new Set(filteredAccesos.map((a) => a.gateZonaId));
                setZonas(resultZonas.filter((z) => z.gateZonaGlobal !== "Y" && availableZonaIds.has(z.gateZonaId)));
                setAccesos(filteredAccesos);
            })();
        }
    }, [credentials, service])

    const currentConfig = rows.length > 0 ? rows[0] : null;
    const isToday = currentConfig?.gateLugarDeviceFecha === FECHA_HOY;

    const handleClick = ({ procede, title, isEdit }) => {
        setModalDetails({ title, procede, isEdit, isOpen: true });
    };

    const closeModal = () => {
        setModalDetails({ isOpen: false });
    };

    return (
        <main className={s.main}>
            <Modal onClose={closeModal} open={modalDetails.isOpen} title={modalDetails.title}>
                {modalDetails.procede === ACTIONS.INSERT && (
                    <InsertModal
                        kioscoId={kioscoId}
                        zonas={zonas}
                        accesos={accesos}
                        currentConfig={modalDetails.isEdit ? currentConfig : null}
                        updateList={updateList}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.DEACTIVATE && (
                    <DesactivarModal
                        kioscoId={kioscoId}
                        kioscoDetail={currentConfig}
                        updateList={updateList}
                        closeModal={closeModal}
                    />
                )}
            </Modal>
            <div className={s.actions}>
                <Buttons>
                    <HeaderButton
                        text={"Actualizar"}
                        onClick={updateList}
                    >
                        <Cached />
                    </HeaderButton>
                    {(!currentConfig || !isToday) && (
                        <HeaderButton
                            text={"Agregar"}
                            handleClick={handleClick}
                            data={{
                                title: `Agregar Acceso - ${kioscoIdentificacion}`,
                                procede: ACTIONS.INSERT,
                                isEdit: false,
                            }}
                        >
                            <Add />
                        </HeaderButton>
                    )}
                    {currentConfig && isToday && (
                        <HeaderButton
                            text={"Editar"}
                            handleClick={handleClick}
                            data={{
                                title: `Editar Acceso - ${kioscoIdentificacion}`,
                                procede: ACTIONS.INSERT,
                                isEdit: true,
                            }}
                        >
                            <EditOutlined />
                        </HeaderButton>
                    )}
                    <HeaderButton
                        text={"Dar de Baja"}
                        handleClick={handleClick}
                        data={{
                            title: `Dar de Baja Acceso - ${kioscoIdentificacion}`,
                            procede: ACTIONS.DEACTIVATE,
                        }}
                        disabled={!currentConfig}
                    >
                        <BlockOutlined />
                    </HeaderButton>
                </Buttons>
                <div className={s.containerData}>
                    <p><b>Número de serie:</b> {kioscoNro}</p>
                </div>
            </div>
            <p style={{ fontWeight: 600, marginBottom: "0.5rem" }}>Acceso Actual</p>
            <div style={{ marginBottom: "1rem" }}>
                <XGrid
                    rows={currentConfig ? [currentConfig] : []}
                    titles={titles}
                    loading={isLoading}
                    secondarySelected={{ status: false, row: {} }}
                    setSecondarySelected={() => {}}
                    hideFooter
                    autoHeight
                />
            </div>
            <p style={{ fontWeight: 600, marginBottom: "0.5rem" }}>Historial de Accesos</p>
            <div className={s.container}>
                <XGrid
                    rows={rows}
                    titles={titles}
                    loading={isLoading}
                    secondarySelected={{ status: false, row: {} }}
                    setSecondarySelected={() => {}}
                />
            </div>
        </main>
    );
}
